import { useState, useEffect, createContext, useContext } from 'react';
import { ADMIN_EMAILS } from '../config';
import { getUser } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // teamsReady is false until the server confirms the user's current team assignments
  const [teamsReady, setTeamsReady] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem('tbco_user');
    if (stored) {
      try {
        const cached = JSON.parse(stored);
        setUser(cached);
        setLoading(false);
        // Always re-fetch teams from server — sessionStorage can be stale
        getUser(cached.email)
          .then(serverUser => {
            if (serverUser) {
              const updated = { ...cached, teams: serverUser.teams || [] };
              setUser(updated);
              sessionStorage.setItem('tbco_user', JSON.stringify(updated));
            }
          })
          .catch(() => {})
          .finally(() => setTeamsReady(true));
        return;
      } catch {}
    }
    setLoading(false);
    setTeamsReady(true);
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const payload = JSON.parse(atob(credentialResponse.credential.split('.')[1]));
      const email = payload.email;
      const name = payload.name;
      const picture = payload.picture;

      const isAdmin = ADMIN_EMAILS.includes(email);

      let role = isAdmin ? 'admin' : 'unknown';
      let teams = [];

      try {
        const serverUser = await getUser(email);
        if (serverUser) {
          role = serverUser.role;
          teams = serverUser.teams || [];
        }
      } catch {
        if (isAdmin) role = 'admin';
      }

      const userObj = { email, name, picture, role, teams };
      setUser(userObj);
      sessionStorage.setItem('tbco_user', JSON.stringify(userObj));
      setTeamsReady(true);
    } catch (err) {
      console.error('Auth error:', err);
    }
  };

  const refreshUser = async () => {
    if (!user?.email) return user;
    try {
      const serverUser = await getUser(user.email);
      if (serverUser) {
        const updated = { ...user, teams: serverUser.teams || [] };
        setUser(updated);
        sessionStorage.setItem('tbco_user', JSON.stringify(updated));
        return updated;
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
    return user;
  };

  const signOut = () => {
    setUser(null);
    setTeamsReady(false);
    sessionStorage.removeItem('tbco_user');
  };

  return (
    <AuthContext.Provider value={{ user, loading, teamsReady, handleGoogleSuccess, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
